import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgeCheck, Heart, Star, ArrowLeft, Check, Flag, Send, Lock, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { prettyCity } from "@/components/CompanionCard";
import { Reveal, EASE_LUXE } from "@/components/Reveal";

export default function Profile() {
  const { slug } = useParams();
  const { user, favSlugs, toggleFavourite, isPremium } = useAuth();
  const [c, setC] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", date: "", message: "" });
  const [sending, setSending] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    face: 5, body: 5, personality: 5, telephone_manner: 5,
    venue: 5, privacy: 5, service: 5,
    will_return: null, photos_match: null, secure_parking: null,
    comment: "",
  });

  const isFav = favSlugs?.includes(slug);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/companions/${slug}`);
      setC(data);
      const r = await api.get(`/companions/${slug}/reviews`);
      setReviews(r.data);
    } catch {
      setNotFound(true);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    load();
  }, [load]);

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { companion_id: slug, ...form });
      toast.success("Enquiry sent. Our concierge will be in touch discreetly.");
      setForm({ name: "", email: "", phone: "", date: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please sign in to leave a review.");
    try {
      await api.post(`/companions/${slug}/reviews`, reviewForm);
      toast.success("Review posted. Thread opened in the Forum.");
      setReviewForm({
        face: 5, body: 5, personality: 5, telephone_manner: 5,
        venue: 5, privacy: 5, service: 5,
        will_return: null, photos_match: null, secure_parking: null,
        comment: "",
      });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const onFav = async () => {
    if (!user) return toast.error("Please sign in to save favourites.");
    try {
      const f = await toggleFavourite(slug);
      toast.success(f ? "Added to favourites" : "Removed from favourites");
    } catch (err) {
      if (err.premium) toast.error("Saving favourites is a Premium feature. Upgrade to unlock.");
      else toast.error("Could not update favourite.");
    }
  };

  const sendMessage = async () => {
    if (!user) return toast.error("Please sign in to send a message.");
    const body = window.prompt(`Message to ${c.name}:`);
    if (!body) return;
    try {
      await api.post("/messages", { companion_slug: slug, body });
      toast.success("Message sent.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const reportProfile = async () => {
    const reason = window.prompt("Why are you reporting this profile?");
    if (!reason) return;
    try {
      await api.post(`/companions/${slug}/report`, { reason });
      toast.success("Report submitted. Thank you.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const waNumber = (c?.contact?.whatsapp || "").replace(/\D/g, "").replace(/^0/, "27");
  const tgHandle = (c?.contact?.telegram || "").replace("@", "");

  if (notFound)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6" data-testid="profile-notfound">
        <h1 className="font-heading text-5xl font-light text-white">Companion not found</h1>
        <Link to="/directory" className="text-xs uppercase tracking-luxe text-velvet-gold">Back to directory</Link>
      </div>
    );

  if (!c) return <div className="flex min-h-screen items-center justify-center text-white/40">Loading…</div>;

  return (
    <div className="pt-28 md:pt-32" data-testid="profile-page">
      <SEO
        title={`${c.name} · ${prettyCity(c.city)}`}
        description={`${c.age ? c.age + " · " : ""}${prettyCity(c.city)}${c.verified ? " · Verified" : ""}${c.rating ? ` · ${c.rating}★` : ""}. ${(c.bio || "").slice(0, 180)}`}
        image={c.hero_image}
        type="profile"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: c.name,
          image: c.hero_image,
          description: c.bio,
          address: { "@type": "PostalAddress", addressLocality: prettyCity(c.city), addressCountry: "ZA" },
          aggregateRating: c.review_count ? {
            "@type": "AggregateRating",
            ratingValue: c.rating,
            reviewCount: c.review_count,
          } : undefined,
        }}
      />
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <Link to="/directory" className="mb-10 inline-flex items-center gap-2 text-xs uppercase tracking-luxe text-white/50 transition-colors hover:text-velvet-gold">
          <ArrowLeft size={14} /> Directory
        </Link>

        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* LEFT — sticky portrait */}
          <div className="lg:col-span-6">
            <div className="lg:sticky lg:top-28">
              <motion.div
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.1, ease: EASE_LUXE }}
                className="clip-frame relative aspect-[3/4] bg-velvet-coal"
              >
                <img src={c.hero_image} alt={c.name} className="h-full w-full object-cover" />
                {c.featured && (
                  <span className="absolute left-5 top-5 bg-velvet-gold px-3 py-1 text-[10px] uppercase tracking-luxe text-black">Featured</span>
                )}
              </motion.div>
              <div className="mt-6 grid grid-cols-3 gap-4" data-testid="profile-gallery">
                {(isPremium ? (c.gallery || []) : (c.gallery || []).slice(0, 2)).map((g, i) => (
                  <div key={i} className="clip-frame aspect-square bg-velvet-coal">
                    <img src={g} alt={`${c.name} ${i + 1}`} className="h-full w-full object-cover" />
                  </div>
                ))}
                {!isPremium && (c.gallery || []).length > 2 && (
                  <Link to="/membership" data-testid="gallery-locked"
                    className="flex aspect-square flex-col items-center justify-center gap-2 border border-velvet-rose/40 bg-velvet-coal text-center">
                    <Lock size={18} className="text-velvet-rose" />
                    <span className="px-2 text-[9px] uppercase tracking-luxe text-velvet-rose">+{(c.gallery || []).length - 2} photos<br/>Go Premium</span>
                  </Link>
                )}
              </div>
              {!isPremium && (
                <p className="mt-3 text-center text-[10px] uppercase tracking-luxe text-white/30">Free members see 3 photos &middot; <Link to="/membership" className="text-velvet-gold">Unlock all</Link></p>
              )}
            </div>
          </div>

          {/* RIGHT — details */}
          <div className="lg:col-span-6">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="font-heading text-6xl font-light leading-none tracking-tight text-white md:text-7xl">{c.name}</h1>
                  {c.verified && <BadgeCheck className="text-velvet-gold" />}
                </div>
                <p className="mt-4 text-xs uppercase tracking-luxe text-velvet-gold">
                  {prettyCity(c.city)} &middot; {c.age} &middot; {c.height}
                </p>
                {c.available_now && (
                  <span className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#25D366]/15 px-3 py-1 text-[10px] uppercase tracking-luxe text-[#25D366]" data-testid="available-now-badge">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#25D366]" /> Available now
                  </span>
                )}
              </div>
              <button
                onClick={onFav}
                data-testid="profile-favourite-button"
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/15 transition-colors hover:border-velvet-rose"
              >
                <Heart size={18} className={isFav ? "fill-velvet-rose text-velvet-rose" : "text-white"} />
              </button>
            </div>

            <p className="mt-6 font-heading text-2xl font-light italic text-white/80">&ldquo;{c.tagline}&rdquo;</p>
            <p className="mt-6 text-base font-light leading-relaxed text-white/60">{c.bio}</p>

            {/* Attributes */}
            <div className="mt-12 grid grid-cols-2 gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
              {[
                ["Category", c.category],
                ["Ethnicity", c.race],
                ["Hair", c.attributes?.hair],
                ["Eyes", c.attributes?.eyes],
                ["Figure", c.attributes?.body],
                ["Serves", [c.incall && "Incall", c.outcall && "Outcall"].filter(Boolean).join(" / ") || "—"],
                ["Languages", (c.languages || []).join(", ")],
                ["Rating", `${c.rating} / 5`],
                ["Reviews", c.review_count],
              ].map(([k, v]) => (
                <div key={k} className="bg-velvet-ink p-5">
                  <p className="text-[10px] uppercase tracking-luxe text-white/40">{k}</p>
                  <p className="mt-2 text-sm text-white">{v || "—"}</p>
                </div>
              ))}
            </div>

            {/* Vibes + Interests */}
            <div className="mt-8 flex flex-wrap gap-3">
              {(c.vibes || []).map((v) => (
                <span key={v} className="bg-velvet-rose px-4 py-2 text-xs uppercase tracking-luxe text-black">{v}</span>
              ))}
              {(c.interests || []).map((it) => (
                <span key={it} className="border border-white/15 px-4 py-2 text-xs uppercase tracking-luxe text-white/70">{it}</span>
              ))}
            </div>

            {/* Contact — message only */}
            <div className="mt-10 flex flex-wrap items-center gap-3" data-testid="contact-actions">
              {isPremium ? (
                <button onClick={sendMessage} data-testid="contact-message"
                  className="flex items-center gap-2 rounded-full bg-velvet-rose px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold">
                  <Send size={15} /> Send a message
                </button>
              ) : (
                <Link to="/membership" data-testid="contact-message-locked"
                  className="flex items-center gap-2 rounded-full border border-velvet-rose/60 px-8 py-4 text-xs uppercase tracking-luxe text-velvet-rose transition-colors hover:bg-velvet-rose hover:text-black">
                  <Send size={15} /> Message &mdash; Premium only
                </Link>
              )}
              <button onClick={reportProfile} data-testid="report-button"
                className="flex items-center gap-2 rounded-full px-4 py-4 text-xs uppercase tracking-luxe text-white/40 transition-colors hover:text-velvet-rose">
                <Flag size={13} /> Report
              </button>
            </div>
            <p className="mt-3 text-xs font-light text-white/40">All contact is via secure on-site messaging only.</p>

            {/* Touring schedule */}
            {(() => {
              const today = new Date().toISOString().slice(0, 10);
              const upcoming = (c.tours || [])
                .filter((t) => (t.end_date || "") >= today)
                .sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
              if (upcoming.length === 0) return null;
              return (
                <div className="mt-16" data-testid="tours-section">
                  <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">
                    Touring schedule
                  </p>
                  <div className="space-y-3">
                    {upcoming.map((t) => {
                      const active = t.start_date <= today && t.end_date >= today;
                      const cityName = (t.city || "").replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
                      return (
                        <div
                          key={t.id || `${t.city}-${t.start_date}`}
                          data-testid={`tour-item-${t.id}`}
                          className={`flex items-start justify-between gap-4 border p-5 transition-colors ${active ? "border-[#25D366]/50 bg-[#25D366]/5" : "border-white/10 hover:border-velvet-rose/40"}`}
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <MapPin size={14} className="text-velvet-rose" />
                              <p className="text-lg font-light text-white">
                                {cityName}{t.town ? ` — ${t.town}` : ""}
                              </p>
                              {active && (
                                <span className="ml-1 rounded-full bg-[#25D366]/15 px-2.5 py-0.5 text-[10px] uppercase tracking-luxe text-[#25D366]">
                                  Live now
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-[10px] uppercase tracking-luxe text-white/50 tabular-nums">
                              {t.start_date} → {t.end_date}
                            </p>
                            {t.note && (
                              <p className="mt-2 text-sm font-light leading-relaxed text-white/60">{t.note}</p>
                            )}
                          </div>
                          <Link
                            to={`/directory?city=${t.city}`}
                            className="whitespace-nowrap text-[10px] uppercase tracking-luxe text-velvet-gold hover:text-white"
                          >
                            View {cityName} →
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Extras — additional services/items not in the main rate */}
            {(c.extras || []).length > 0 && (
              <div className="mt-16" data-testid="extras-section">
                <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">Extras not included</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {c.extras.map((ex, i) => (
                    <div key={i} data-testid={`extras-item-${i}`} className="flex items-start justify-between gap-4 border border-white/10 p-4 transition-colors hover:border-velvet-gold">
                      <div className="min-w-0">
                        <p className="text-sm text-white">{ex.name}</p>
                        {ex.note && <p className="mt-1 text-xs font-light text-white/50">{ex.note}</p>}
                      </div>
                      {ex.price && (
                        <p className="whitespace-nowrap text-sm text-velvet-gold">{ex.price}</p>
                      )}
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[10px] uppercase tracking-luxe text-white/40">
                  Extras are quoted separately and are subject to discussion.
                </p>
              </div>
            )}

            {/* Reviews */}
            <div className="mt-16" data-testid="reviews-section">
              <p className="mb-8 text-xs uppercase tracking-luxe text-velvet-gold">Reviews ({reviews.length})</p>
              <div className="space-y-10">
                {reviews.map((r) => {
                  const crit = r.criteria || {};
                  const critEntries = Object.entries(crit).filter(([, v]) => v != null);
                  const yesNo = (v) => v === true ? "Yes" : v === false ? "No" : null;
                  const qa = [
                    ["Will return", yesNo(r.will_return)],
                    ["Photos match", yesNo(r.photos_match)],
                    ["Secure parking", yesNo(r.secure_parking)],
                  ].filter(([, v]) => v !== null);
                  return (
                    <div key={r.id} className="border-t border-white/10 pt-6" data-testid={`review-item-${r.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white">{r.user_name}</span>
                        <span className="flex items-center gap-1 text-xs text-velvet-gold">
                          {Array.from({ length: r.rating }).map((_, i) => (
                            <Star key={i} size={12} className="fill-velvet-gold" />
                          ))}
                        </span>
                      </div>
                      {critEntries.length > 0 && (
                        <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
                          {critEntries.map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-xs">
                              <span className="capitalize text-white/40">{k.replace(/_/g, " ")}</span>
                              <span className="tabular-nums text-velvet-gold">{v}/5</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {qa.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {qa.map(([k, v]) => (
                            <span
                              key={k}
                              className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-luxe ${
                                v === "Yes"
                                  ? "border-[#25D366]/50 text-[#25D366]"
                                  : "border-velvet-rose/50 text-velvet-rose"
                              }`}
                            >
                              {k}: {v}
                            </span>
                          ))}
                        </div>
                      )}
                      <p className="mt-4 text-sm font-light leading-relaxed text-white/60">{r.comment}</p>
                      {r.forum_thread_id && (
                        <Link
                          to={`/forum/${r.forum_thread_id}`}
                          data-testid={`review-forum-link-${r.id}`}
                          className="mt-3 inline-block text-[10px] uppercase tracking-luxe text-velvet-gold hover:text-white"
                        >
                          Discuss in forum &rarr;
                        </Link>
                      )}
                    </div>
                  );
                })}
                {reviews.length === 0 && <p className="text-sm font-light text-white/40">No reviews yet.</p>}
              </div>

              {user && isPremium ? (
                <form onSubmit={submitReview} className="mt-10 border-t border-white/10 pt-8" data-testid="review-form">
                  <p className="mb-6 text-[10px] uppercase tracking-luxe text-velvet-gold">Rate each dimension (1–5)</p>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
                    {[
                      ["face", "Face"],
                      ["body", "Body"],
                      ["personality", "Personality"],
                      ["telephone_manner", "Telephone Manner"],
                      ["venue", "Venue"],
                      ["privacy", "Privacy"],
                      ["service", "Service"],
                    ].map(([key, label]) => (
                      <div key={key} className="flex items-center justify-between gap-4">
                        <label className="text-xs uppercase tracking-luxe text-white/60">{label}</label>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((n) => (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, [key]: n })}
                              data-testid={`review-${key}-${n}`}
                            >
                              <Star
                                size={16}
                                className={n <= reviewForm[key] ? "fill-velvet-gold text-velvet-gold" : "text-white/20"}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="mb-4 mt-8 text-[10px] uppercase tracking-luxe text-velvet-gold">Quick check</p>
                  <div className="space-y-4">
                    {[
                      ["will_return", "Would you return?"],
                      ["photos_match", "Do the photos match the person?"],
                      ["secure_parking", "Was there secure parking?"],
                    ].map(([key, label]) => (
                      <div key={key} className="flex items-center justify-between gap-4">
                        <span className="text-xs uppercase tracking-luxe text-white/60">{label}</span>
                        <div className="flex gap-2">
                          {[
                            { label: "Yes", val: true },
                            { label: "No", val: false },
                          ].map((opt) => (
                            <button
                              key={opt.label}
                              type="button"
                              onClick={() => setReviewForm({ ...reviewForm, [key]: opt.val })}
                              data-testid={`review-${key}-${opt.label.toLowerCase()}`}
                              className={`rounded-full border px-4 py-1.5 text-[10px] uppercase tracking-luxe transition-colors ${
                                reviewForm[key] === opt.val
                                  ? opt.val
                                    ? "border-[#25D366] bg-[#25D366]/10 text-[#25D366]"
                                    : "border-velvet-rose bg-velvet-rose/10 text-velvet-rose"
                                  : "border-white/15 text-white/50 hover:border-white/40"
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <textarea
                    required
                    rows={4}
                    placeholder="Share your experience"
                    value={reviewForm.comment}
                    data-testid="review-comment"
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    className="mt-8 w-full border-b border-white/15 bg-transparent py-3 text-sm text-white placeholder:text-white/30 focus:border-velvet-gold focus:outline-none"
                  />
                  <button
                    type="submit"
                    data-testid="review-submit"
                    className="mt-6 rounded-full bg-velvet-rose px-8 py-3 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold"
                  >
                    Post review &amp; open forum thread
                  </button>
                  <p className="mt-3 text-[10px] uppercase tracking-luxe text-white/30">
                    Your review will also open a public thread under Forum &rsaquo; Reviews.
                  </p>
                </form>
              ) : (
                <p className="mt-8 border-t border-white/10 pt-8 text-sm font-light text-white/40">
                  {user ? (
                    <><Link to="/membership" className="text-velvet-gold">Upgrade to Premium</Link> to write a review.</>
                  ) : (
                    <><Link to="/login" className="text-velvet-gold">Sign in</Link> to leave a review.</>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
